/**
 * Staff — the admin's fifth tab (T2075 M5; plan R-6, R-7, R-8, R-16, Step 5,
 * A4, A7, A9, D11, D12). Two sections behind a section strip:
 *
 *   · **Staff members** — the roster a request is asked for by: add (the
 *     screen's ONE gold primary, in the add row FIRST, above the import row
 *     and the table, so neither a 130-row list nor an import report pushes it
 *     below the 700 plane, R-10 / R11); edit name or group in place;
 *     deactivate (armed in place) and reactivate; import a CSV of name and
 *     group with a report of what was added, skipped, rejected and ignored;
 *     export the list with each member's last-requested date.
 *   · **Sign-in accounts** — every account of the home with mailbox, type,
 *     group, active and PIN state; add a group or reception sign-in by
 *     mailbox (the group picked from the staff labels, D2); deactivate a group
 *     or reception account (armed); reset any account's PIN (armed, with the
 *     copy that says everyone signed in as it is signed out).
 *     ⭐ T2075 M6 (the M5 ux-review carry) — THIS TABLE'S ARMED CONFIRMS
 *     RENDER IN ONE FULL-WIDTH BLOCK BETWEEN THE HEADING AND THE TABLE
 *     (`.staff-confirm`), never inside the Actions cell: a 90-character
 *     sentence plus two buttons in a narrow cell grew the armed row to
 *     74 px and the review measured Yes / Cancel at 655–729 on a six-row
 *     list at 1024×700, below the plane. The block's height is now
 *     independent of the row count and of which row is armed, and the armed
 *     row is the one whose action button is gone. Focus moves to the block
 *     (the row's button it replaced is unmounted, so there is nothing to
 *     return to — the `MyResidents` heading mechanism). The members table's
 *     armed Deactivate stays in its row: the copy is two words and the
 *     column is wide.
 *
 * ⛔ THE ORDER ON EACH SECTION IS A PLANE DECISION (R11, the M5 ux-review):
 *   · members: add row → import/export row → the import's notices and its
 *     rejected-lines table → heading → table. The review measured the Add
 *     primary at top 768 after a 25-row import when the report sat above the
 *     add row (647 at rest, already on the edge); nothing of unbounded height
 *     now precedes the primary. Re-derive on the seat at 1024x700:
 *     `document.querySelector('.btn--primary').getBoundingClientRect()`.
 *   · accounts: heading → (the armed confirm block) → table → add row. The
 *     accounts Add is SECONDARY, so R11 does not place it, and the list is
 *     short by nature (a handful of sign-ins per home); putting it under the
 *     table lifts the table by the add row's height (an armed confirm in the
 *     row measured at 722 to 766 with the add row above the table at M5; since
 *     T2075 M6 the confirm is above the table and no longer in any row).
 *
 * ⛔ A FIELD'S STANDING NOTE IS RENDERED UNDER THE ROW, NOT INSIDE THE FIELD,
 * and it is still the field's (`describedBy`). `.staff-row` is a grid aligned
 * on the items' bottoms so one button sits on the fields' baseline; a hint
 * inside one field made that wrapper taller than its neighbour, and the review
 * measured the Group control 51px above the Name control (top 566 against 617
 * at 1024). Every field in a row is now label + control and nothing else, the
 * row's note follows it as a `field-hint` paragraph with its own id, and the
 * field references that id through `aria-describedby` exactly as an in-field
 * hint would be: a reader still hears it as part of the field.
 *
 * A quiet **Download access review** at the foot (A7): the home's own review.
 *
 * ⛔ THE LABELS ARE DISTINCT ACROSS THE WHOLE VIEW (`staffCopy`): **Group**
 * (add row), **Change group** (the edit row), **Sign-in group** (the accounts
 * add row), **Sign-in type**; `getByLabel` never hits strict-mode ambiguity.
 *
 * ⛔ THE BROWSER PARSES THE CSV, THE SERVER VALIDATES EVERY CELL (D11):
 * `csv-import.ts` decodes, sniffs and shapes the rows; the server cleans, caps
 * and refuses. A file over the row cap is refused HERE with the count, before
 * a byte is posted.
 *
 * ⭐ THE SCREEN COMPOSES `@ecmb/ui` AND DRAWS NOTHING: `Table` (framed), the
 * `file` `FormField` (the kind this milestone added to the catalog first),
 * `FormField` with `suggestions` for the group, `Notice` for the import report
 * and its warnings, `Table`'s own `EmptyState`, `Button`, `Chip`. The class-level debt the
 * census reads off this composition (`field-label` + `login-input` per
 * `FormField`, `chip` per removed-state chip) is declared as measured in
 * `test/client/lib/adoption-debt.ts`.
 */
import { useEffect, useRef, useState } from 'preact/hooks';
import type { ApiClient } from '../api.js';
import { sharedCopy, staffCopy } from '../copy.js';
import { decodeCsvFile, type CsvImportPlan } from '../csv-import.js';
import { parseActiveCell } from '../../core/csv.js';
import { cleanStoredText } from '../../shared/request-schema.js';
import type { StaffImportReport } from '../../core/staff-repo.js';
import { Button, Chip, FormField, Notice, Table, TableScroll } from '@ecmb/ui';

export type StaffRowItem = { staffId: number; displayName: string; groupLabel: string; active: boolean; lastRequestedAt: string | null };
export type AccountItem = { id: number; email: string; displayName: string; role: string; groupLabel: string | null; active: boolean; hasPin: boolean };

type StaffResponse = { staff: StaffRowItem[]; groups: string[] };
type AccountsResponse = { accounts: AccountItem[] };
type Section = 'members' | 'accounts';
type Armed = { kind: 'staff'; id: number } | { kind: 'account'; id: number } | { kind: 'pin'; id: number } | null;

/** The Blob-and-anchor download the ops review screen uses (`AccessReview.tsx`). */
function downloadText(fileName: string, text: string): void {
  const blob = new Blob([text], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const today = (): string => new Date().toISOString().slice(0, 10);

/** The mailbox field's cap: the server's `NURSE_EMAIL_MAX_LEN` (RFC 5321's
 *  address maximum), which a client bundle cannot import from a server module.
 *  Re-derive: `/usr/bin/grep -n "NURSE_EMAIL_MAX_LEN = " portal/src/server/admin-routes.ts`;
 *  `staff.spec.tsx` asserts the two are equal. ⛔ EXPLICIT, because a
 *  `FormField` with no cap once rendered `maxlength="0"` here (the M5
 *  webapp-testing regression; the library fix is in `FormField.tsx`). */
const MAILBOX_MAX_LEN = 254;

/** The row notes' ids (each field references its note through `describedBy`). */
const IMPORT_NOTE_ID = 'staff-import-note';
const ADD_GROUP_NOTE_ID = 'staff-add-group-note';
const ACCOUNT_GROUP_NOTE_ID = 'staff-account-group-note';

export type StaffProps = { api: ApiClient };

export function Staff({ api }: StaffProps) {
  const [section, setSection] = useState<Section>('members');
  const [staff, setStaff] = useState<StaffRowItem[] | null>(null);
  const [groups, setGroups] = useState<string[]>([]);
  const [accounts, setAccounts] = useState<AccountItem[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [retry, setRetry] = useState(0);
  // One status line, always present, for the outcome of the last act.
  const [status, setStatus] = useState<string | null>(null);
  const [actError, setActError] = useState<string | null>(null);
  const [armed, setArmed] = useState<Armed>(null);

  // Members: the add row, the edit row and the import report.
  const [addName, setAddName] = useState('');
  const [addGroup, setAddGroup] = useState('');
  const [addNameError, setAddNameError] = useState<string | undefined>(undefined);
  const [addGroupError, setAddGroupError] = useState<string | undefined>(undefined);
  const [editing, setEditing] = useState<{ id: number; name: string; group: string } | null>(null);
  const [report, setReport] = useState<{ report: StaffImportReport; encoding: string } | null>(null);
  const [importRefusal, setImportRefusal] = useState<string | null>(null);
  /** ⭐ T2173 — the chosen file's NAME, held by the screen because the control
   *  cannot hold it: `FormField kind="file"` clears `input.value` after every
   *  change (so re-choosing the same file fires again, T2075 M6), which also
   *  wipes the native "1 file chosen" text the browser would otherwise show.
   *  Alex read the bare control as a draft. Set synchronously in `importFile`,
   *  before its first await, for the same reason `files[0]` is. */
  const [chosenFile, setChosenFile] = useState<string | null>(null);
  /** ⭐⭐ T2173 rework 1 (security Medium) — a decoded plan held back for the
   *  admin's say-so because it would deactivate people. `null` whenever nothing
   *  is waiting; an import that deactivates NOBODY never lands here and posts
   *  exactly as it always did. */
  const [pendingImport, setPendingImport] = useState<{ plan: CsvImportPlan; deactivations: number } | null>(null);

  // Accounts: the add row.
  const [mailbox, setMailbox] = useState('');
  const [accountType, setAccountType] = useState<'group' | 'reception'>('group');
  const [accountGroup, setAccountGroup] = useState('');
  const [mailboxError, setMailboxError] = useState<string | undefined>(undefined);
  const [accountGroupError, setAccountGroupError] = useState<string | undefined>(undefined);

  // ⛔ EDIT-IN-PLACE MOVES FOCUS TO THE FIRST EDITABLE FIELD (the M5 ux-review):
  // the Edit button's DOM node is reused by Preact for the Save button at that
  // grid position, so without this the focus landed on Save. An effect keyed
  // on the row being edited, not a callback ref: an inline ref runs on every
  // render and would pull focus back to the name while the group is typed in.
  const editNameRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (editing !== null) editNameRef.current?.focus();
  }, [editing?.id]);

  // ⭐ T2075 M6 — the accounts table's armed confirm renders ABOVE the table
  // (the header docblock says why), so the button that armed it is unmounted
  // and focus would fall to the body; the block takes it (`tabIndex={-1}`,
  // the `MyResidents` heading mechanism), so Tab reaches Yes, then Cancel.
  // ⭐ T2173 rework 1 — the import's own armed confirm shares this ref and this
  // mechanism. It is SAFE to share: the two blocks live in different sections and
  // `section === 'members' ? members : accountsSection` mounts exactly one. The
  // import confirm needs it for the same reason the accounts one does — the
  // control that armed it is a file input whose value was just cleared, so focus
  // would otherwise sit on a chooser that no longer describes the pending act.
  const confirmRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (armed !== null && armed.kind !== 'staff') confirmRef.current?.focus();
  }, [armed?.kind, armed?.id]);
  useEffect(() => {
    if (pendingImport !== null) confirmRef.current?.focus();
  }, [pendingImport?.deactivations, pendingImport === null]);

  const load = async (): Promise<void> => {
    setLoadError(null);
    const [s, a] = await Promise.all([api.get<StaffResponse>('/api/admin/staff'), api.get<AccountsResponse>('/api/admin/accounts')]);
    if (s.kind === 'network-error' || a.kind === 'network-error') {
      setLoadError(sharedCopy.networkError);
      return;
    }
    if (s.status !== 200 || a.status !== 200) {
      setLoadError(staffCopy.loadError);
      return;
    }
    setStaff(s.body.staff);
    setGroups(s.body.groups);
    setAccounts(a.body.accounts);
  };
  useEffect(() => {
    void load();
  }, [retry]);

  // The default group for a new sign-in is the first label once the list is known.
  useEffect(() => {
    if (accountGroup === '' && groups.length > 0) setAccountGroup(groups[0]!);
  }, [groups]);

  /** Every write: on success reload the lists and say what happened. */
  async function act(path: string, body: unknown, done: string): Promise<{ status: number; body: unknown } | null> {
    setActError(null);
    const res = await api.post<unknown>(path, body);
    if (res.kind === 'network-error') {
      setActError(sharedCopy.networkError);
      return null;
    }
    if (res.status === 200 || res.status === 201) {
      setStatus(done);
      await load();
    }
    return { status: res.status, body: res.body };
  }

  // --- Members --------------------------------------------------------------

  async function addMember(): Promise<void> {
    setAddNameError(undefined);
    setAddGroupError(undefined);
    const res = await act('/api/admin/staff', { displayName: addName, groupLabel: addGroup }, staffCopy.memberAdded(addName.trim()));
    if (res === null) return;
    if (res.status === 201) {
      setAddName('');
      setAddGroup('');
      return;
    }
    const error = (res.body as { error?: string } | undefined)?.error ?? '';
    if (error.startsWith('name_')) setAddNameError(staffCopy.reasons[error] ?? staffCopy.saveError);
    else if (error.startsWith('group_')) setAddGroupError(staffCopy.reasons[error] ?? staffCopy.saveError);
    else setActError(staffCopy.saveError);
  }

  async function saveEdit(): Promise<void> {
    if (editing === null) return;
    const res = await act(`/api/admin/staff/${String(editing.id)}`, { displayName: editing.name, groupLabel: editing.group }, staffCopy.memberSaved(editing.name.trim()));
    if (res === null) return;
    if (res.status === 200) setEditing(null);
    else setActError((res.body as { error?: string } | undefined)?.error !== undefined ? staffCopy.reasons[(res.body as { error: string }).error] ?? staffCopy.saveError : staffCopy.saveError);
  }

  async function setMemberActive(item: StaffRowItem, active: boolean): Promise<void> {
    setArmed(null);
    const res = await act(`/api/admin/staff/${String(item.staffId)}/active`, { active }, active ? staffCopy.memberRestored(item.displayName) : staffCopy.memberRemoved(item.displayName));
    if (res !== null && res.status !== 200) setActError(staffCopy.saveError);
  }

  async function importFile(files: FileList): Promise<void> {
    const file = files[0];
    if (file === undefined) return;
    // ⛔ THROUGH THE HOUSE CLEANER, like every other file-derived string that
    // reaches this admin (the imported cells, the echoed `ignoredColumns`, the
    // group labels). `cleanStoredText` strips `\p{Bidi_Control}` because of a
    // MEASURED incident (board 1868/1873, 2026-09-08) where a U+200F rendered a
    // phone number reversed; a filename is the same class — `roster\u202Efdp.csv`
    // displays as `rosterlmth.csv` and would show the admin a name that is not
    // the file she picked. Synchronous, before the first await, so the
    // `FileList` is still the one the change event handed us.
    setChosenFile(cleanStoredText(file.name));
    setImportRefusal(null);
    setReport(null);
    // ⛔ ONE LIVE REGION PER EVENT. The import's outcome is announced by the
    // report `Notice` (itself `role="status"`) or the refusal `Notice`
    // (`role="alert"`), so the hidden status line is CLEARED here and never
    // set for an import: two regions carrying the same lead were a double
    // announcement (the driver's gate 4, 2026-09-14).
    setStatus(null);
    setPendingImport(null);
    const plan = await decodeCsvFile(file);
    if ('kind' in plan) {
      setImportRefusal(
        plan.kind === 'too_many_rows' ? staffCopy.refusals.too_many_rows(plan.rows, plan.max) : staffCopy.refusals[plan.kind],
      );
      return;
    }
    /* ⭐⭐ T2173 rework 1 (security Medium) — COUNT THE DEACTIVATIONS BEFORE
       POSTING, and if there are any, ask. The per-row Deactivate has always been
       two-step armed; this path could set up to `STAFF_IMPORT_MAX_ROWS` names
       inactive on a change event, and the requester picker lists ACTIVE names
       only — so a re-imported stale export could empty every nurse's requester
       list with no step in between. The bulk path had the per-row path's
       authority and none of its ceremony.
       ⛔ COUNTED WITH THE SERVER'S OWN PARSER (`parseActiveCell`, moved to
       `core/csv.ts` for exactly this), so the number in the sentence cannot
       disagree with what the repo will do. It is a COUNT and a question, never a
       verdict: `staff-repo` still parses every cell again and still refuses by
       line (D11), so a doctored fetch that skips this gains nothing.
       ⚠️ It counts rows the file asks to deactivate, NOT rows that will change
       state — the browser cannot know which are already inactive without the
       roster, and over-stating by naming a name already inactive is the safe
       direction for a confirm. */
    const deactivations = plan.rows.filter((r) => parseActiveCell(r.active) === false).length;
    if (deactivations > 0) {
      setPendingImport({ plan, deactivations });
      return;
    }
    await postImport(plan);
  }

  /** The POST itself, so the armed confirm and the no-deactivation path run the
   *  SAME code — a second copy is how the two branches drift. */
  async function postImport(plan: CsvImportPlan): Promise<void> {
    setPendingImport(null);
    const res = await api.post<{ report: StaffImportReport; error?: string; rows?: number; max?: number }>('/api/admin/staff/import', {
      rows: plan.rows,
      ignoredColumns: plan.ignoredColumns,
    });
    if (res.kind === 'network-error') {
      setActError(sharedCopy.networkError);
      return;
    }
    if (res.status !== 200) {
      setImportRefusal(
        res.body.error === 'too_many_rows' && res.body.rows !== undefined && res.body.max !== undefined
          ? staffCopy.refusals.too_many_rows(res.body.rows, res.body.max)
          : staffCopy.saveError,
      );
      return;
    }
    setReport({ report: res.body.report, encoding: plan.encoding });
    await load();
  }

  async function exportCsv(): Promise<void> {
    const res = await api.get<{ csv: string }>('/api/admin/staff/export');
    if (res.kind !== 'response' || res.status !== 200) {
      setActError(staffCopy.saveError);
      return;
    }
    downloadText(staffCopy.exportFileName(today()), res.body.csv);
  }

  async function downloadReview(): Promise<void> {
    const res = await api.get<{ csv: string }>('/api/admin/access-review');
    if (res.kind !== 'response' || res.status !== 200) {
      setActError(staffCopy.saveError);
      return;
    }
    downloadText(staffCopy.reviewFileName(today()), res.body.csv);
  }

  // --- Accounts -------------------------------------------------------------

  async function addAccount(): Promise<void> {
    setMailboxError(undefined);
    setAccountGroupError(undefined);
    const body = accountType === 'group' ? { email: mailbox, role: 'group', groupLabel: accountGroup } : { email: mailbox, role: 'reception' };
    const res = await act('/api/admin/accounts', body, staffCopy.accountAdded(mailbox.trim().toLowerCase()));
    if (res === null) return;
    if (res.status === 201) {
      setMailbox('');
      return;
    }
    const b = res.body as { error?: string; errors?: Record<string, string> } | undefined;
    if (res.status === 409) setMailboxError(staffCopy.accountEmailTaken);
    else if (res.status === 429) setActError(staffCopy.accountCreateCapped);
    else if (b?.error === 'role_not_allowed') setActError(staffCopy.accountAdminNotAllowed);
    else if (b?.errors !== undefined) {
      if (b.errors['email'] !== undefined) setMailboxError(b.errors['email']);
      if (b.errors['groupLabel'] !== undefined) setAccountGroupError(b.errors['groupLabel']);
      if (b.errors['email'] === undefined && b.errors['groupLabel'] === undefined) setActError(staffCopy.saveError);
    } else setActError(staffCopy.saveError);
  }

  async function setAccountActive(item: AccountItem, active: boolean): Promise<void> {
    setArmed(null);
    const res = await act(`/api/admin/accounts/${String(item.id)}/active`, { active }, active ? staffCopy.accountReactivated(item.email) : staffCopy.accountDeactivated(item.email));
    if (res !== null && res.status !== 200) setActError(staffCopy.saveError);
  }

  async function resetPin(item: AccountItem): Promise<void> {
    setArmed(null);
    const res = await act(`/api/admin/accounts/${String(item.id)}/pin-reset`, {}, staffCopy.pinReset(item.email));
    if (res !== null && res.status !== 200) setActError(staffCopy.saveError);
  }

  // --- Render ---------------------------------------------------------------

  if (loadError) {
    return (
      <div class="screen staff-screen">
        <h1>{staffCopy.title}</h1>
        <p class="screen-error" role="alert">
          {loadError}
        </p>
        <Button variant="primary" onClick={() => setRetry((n) => n + 1)}>
          {sharedCopy.tryAgain}
        </Button>
      </div>
    );
  }

  const groupOptions = groups.map((g) => ({ value: g, label: g }));

  const memberRow = (item: StaffRowItem) => {
    const isEditing = editing !== null && editing.id === item.staffId;
    const isArmed = armed !== null && armed.kind === 'staff' && armed.id === item.staffId;
    const name = isEditing ? (
      <FormField
        id="staff-edit-name"
        label={staffCopy.editNameLabel}
        value={editing.name}
        onInput={(v) => setEditing({ ...editing, name: v })}
        maxLength={120}
        elementRef={editNameRef}
        opaque
      />
    ) : (
      <>
        {item.displayName}
        {!item.active && (
          <>
            {' '}
            <Chip neutral>{staffCopy.inactive}</Chip>
          </>
        )}
      </>
    );
    const group = isEditing ? (
      <FormField
        id="staff-edit-group"
        label={staffCopy.editGroupLabel}
        value={editing.group}
        onInput={(v) => setEditing({ ...editing, group: v })}
        suggestions={groups}
        maxLength={60}
      />
    ) : (
      item.groupLabel
    );
    const last = item.lastRequestedAt === null ? staffCopy.never : item.lastRequestedAt.slice(0, 10);
    /* ⭐ T2173 rework 1 — ONE ACTIONS ROW PER MEMBER, the same grammar the accounts
       table now uses: `.staff-actions` (nowrap, right-aligned), the ordinary action
       `secondary` and the destructive one `destructive-quiet`. They were bare
       siblings separated by `{' '}` and stacked per row — the shape Alex called
       messy on the accounts table, still live here.
       ⚠️ THE ARMED BRANCH KEEPS ITS SENTENCE IN THE ROW, deliberately, and is the
       one branch that may wrap: the accounts table moved its confirm ABOVE the
       table because a 90-character sentence in a narrow cell grew the row to 74px
       and pushed the buttons below the 700 plane (T2075 M6), but this table's
       confirm is two words in a wide column, which is exactly why M6 left it here.
       ⚠️ `secondary` on Edit is per Captain's "Edit ordinary, aligned like the
       accounts table". It adds navy weight once per ROW, and this table can run to
       the import cap — worth a look in the captures at a long roster. */
    const actions = isEditing ? (
      <div class="staff-actions">
        <Button variant="secondary" size="inline" onClick={() => void saveEdit()}>
          {staffCopy.save}
        </Button>
        <Button variant="quiet" size="inline" onClick={() => setEditing(null)}>
          {staffCopy.cancel}
        </Button>
      </div>
    ) : isArmed ? (
      <div class="staff-actions">
        <span>{staffCopy.deactivateArmed(item.displayName)}</span>
        <Button variant="destructive-quiet" size="inline" onClick={() => void setMemberActive(item, false)}>
          {staffCopy.confirmDeactivate}
        </Button>
        <Button variant="quiet" size="inline" onClick={() => setArmed(null)}>
          {staffCopy.cancel}
        </Button>
      </div>
    ) : item.active ? (
      <div class="staff-actions">
        <Button
          variant="secondary"
          size="inline"
          ariaLabel={staffCopy.editNameFor(item.displayName)}
          onClick={() => {
            setArmed(null);
            setEditing({ id: item.staffId, name: item.displayName, group: item.groupLabel });
          }}
        >
          {staffCopy.edit}
        </Button>
        <Button
          variant="destructive-quiet"
          size="inline"
          ariaLabel={staffCopy.deactivateFor(item.displayName)}
          onClick={() => {
            setEditing(null);
            setArmed({ kind: 'staff', id: item.staffId });
          }}
        >
          {staffCopy.deactivate}
        </Button>
      </div>
    ) : (
      <div class="staff-actions">
        <Button variant="quiet" size="inline" ariaLabel={staffCopy.reactivateFor(item.displayName)} onClick={() => void setMemberActive(item, true)}>
          {staffCopy.reactivate}
        </Button>
      </div>
    );
    return { key: String(item.staffId), cells: [name, group, last, actions] };
  };

  const members = (
    <>
      <p class="field-hint">{staffCopy.membersIntro}</p>
      {/* ⛔ THE ADD ROW COMES FIRST (R11): the screen's one gold primary, above
          the import row and its report so nothing of unbounded height precedes
          it. The header docblock carries the measurement. */}
      <div class="staff-row">
        <FormField
          id="staff-add-name"
          label={staffCopy.addNameLabel}
          value={addName}
          onInput={setAddName}
          error={addNameError}
          maxLength={120}
          opaque
        />
        <FormField
          id="staff-add-group"
          label={staffCopy.addGroupLabel}
          value={addGroup}
          onInput={setAddGroup}
          suggestions={groups}
          describedBy={[ADD_GROUP_NOTE_ID]}
          error={addGroupError}
          maxLength={60}
        />
        <Button variant="primary" onClick={() => void addMember()}>
          {staffCopy.addButton}
        </Button>
      </div>
      <p class="field-hint" id={ADD_GROUP_NOTE_ID}>
        {staffCopy.addGroupHint}
      </p>
      <div class="staff-row">
        <FormField
          id="staff-import"
          kind="file"
          label={staffCopy.importLabel}
          accept=".csv,text/csv,text/plain"
          describedBy={[IMPORT_NOTE_ID]}
          value=""
          onFiles={(files) => void importFile(files)}
        />
        <Button variant="secondary" onClick={() => void exportCsv()}>
          {staffCopy.exportButton}
        </Button>
      </div>
      {/* ⭐ T2173 — the chosen file's name rides the HINT LINE, not a third grid
          item, and that is a correction of this task's own first attempt.
          `.staff-row` is `repeat(auto-fit, minmax(180px, 1fr))`, so a span beside
          the field added a TRACK: choosing a file re-proportioned the row from
          2 x 402px to 3 x 264px, which shoved the unrelated `Export CSV` 138px to
          the right and squeezed the native control until it clipped its own
          status text to "No fil…osen" — while the span 12px away named the file.
          The remedy read more like a draft than the thing it was fixing.
          ⛔ It also belongs here on its own merits: this paragraph is already the
          field's `aria-describedby` target, so the name is announced AS PART OF
          the control rather than as a loose sibling a screen reader meets later. */}
      <p class="field-hint" id={IMPORT_NOTE_ID}>
        {staffCopy.importHint}
        {chosenFile !== null && <span class="staff-file-name">{`${staffCopy.chosenFilePrefix}${chosenFile}`}</span>}
      </p>
      {importRefusal !== null && <Notice kind="error">{importRefusal}</Notice>}
      {/* ⭐⭐ T2173 rework 1 (security Medium) — the bulk deactivation's armed
          confirm, the SAME `.staff-confirm` block the accounts table arms with,
          so the two consequential acts on this screen ask in one grammar. It
          renders only when the file would deactivate somebody; an import that
          deactivates nobody never reaches here. */}
      {pendingImport !== null && (
        <div class="staff-confirm" ref={confirmRef} tabIndex={-1}>
          <span>{staffCopy.importArmed(pendingImport.deactivations)}</span>
          <Button variant="destructive-quiet" size="inline" onClick={() => void postImport(pendingImport.plan)}>
            {staffCopy.confirmImport}
          </Button>
          <Button variant="quiet" size="inline" onClick={() => setPendingImport(null)}>
            {staffCopy.keepImport}
          </Button>
        </div>
      )}
      {report !== null && (
        <>
          <Notice kind="info">
            {staffCopy.reportTitle} {staffCopy.reportLine(report.report.added, report.report.skipped.length, report.report.rejected.length)}
            {report.report.ignoredColumns.length > 0 ? ` ${staffCopy.reportIgnored(report.report.ignoredColumns)}` : ''}
            {report.report.alsoInAnotherGroup.length > 0 ? ` ${staffCopy.reportAlsoInAnotherGroup(report.report.alsoInAnotherGroup.length)}` : ''}
            {` ${staffCopy.reportEncoding(report.encoding)}`}
          </Notice>
          {/* ⭐⭐ T2173 (security-review M1) — A STATE CHANGE GETS ITS OWN `warn`,
              ABOVE the new-groups notice, with the count LEADING. It was a clause
              appended mid-sentence inside the `info` notice, after "ignored
              columns" — so the most consequential outcome this endpoint can
              produce was reported more quietly than a misspelt group label, which
              already earns a `warn`. One upload can flip up to
              `STAFF_IMPORT_MAX_ROWS` rows, and the requester picker lists ACTIVE
              rows only, so an admin re-importing a stale export can empty every
              nurse's requester list. The append-only trail is the forensic
              control; this notice is the one that has to be READ. */}
          {staffCopy.reportStateChanges(report.report.deactivated, report.report.reactivated) !== '' && (
            <Notice kind="warn">{staffCopy.reportStateChanges(report.report.deactivated, report.report.reactivated)}</Notice>
          )}
          {report.report.newGroups.length > 0 && <Notice kind="warn">{staffCopy.reportNewGroups(report.report.newGroups)}</Notice>}
          {report.report.rejected.length > 0 && (
            <Table
              label={staffCopy.rejectedTableLabel}
              columns={[{ head: staffCopy.colLine }, { head: staffCopy.colReason }]}
              rows={report.report.rejected.map((r) => ({
                key: String(r.line),
                cells: [String(r.line), staffCopy.reasons[r.reason] ?? r.reason],
              }))}
            />
          )}
        </>
      )}
      {/* The heading is the separator between the rows above and the table, so
          an armed Deactivate in the table is never block-adjacent to the primary. */}
      <h2 class="my-requests-group">{staffCopy.sectionMembers}</h2>
      {staff === null ? (
        <p class="loading-notice" role="status">
          {staffCopy.membersLoading}
        </p>
      ) : (
        /* ⛔ A NAMED, REACHABLE SCROLL REGION (the M5 ux-review): at 390 the
           four-column table measured 512px wide with no ancestor owning the
           overflow, so the whole page scrolled sideways. `TableScroll` is the
           library's region for exactly this (`role="region"`, focusable, named). */
        <TableScroll label={staffCopy.membersScrollLabel}>
          <Table
            label={staffCopy.membersTableLabel}
            columns={[{ head: staffCopy.colName }, { head: staffCopy.colGroup }, { head: staffCopy.colLastRequested }, { head: staffCopy.colActions }]}
            rows={staff.map(memberRow)}
            empty={{ headline: staffCopy.membersEmpty }}
          />
        </TableScroll>
      )}
      {/* ⭐ T2173 rework 1 — MEMBERS ONLY, on Captain's ruling from the live drive
          (it still rendered under Sign-in accounts, capture 2173-accounts-1024).
          ⚠️ RECORDED ONCE, because the data disagrees with the placement and a
          later reader will hit it: `core/access-review.ts` selects
          `FROM portal.nurses` with `Work email` / `Sign-in type` / `Can sign in`
          columns and ALSO reads `portal.staff_events`, so the review covers both
          tabs. It is reachable from this tab, which is the one an admin manages
          the roster from, and CLAUDE.md rules it a legal control — so if it ever
          needs to be reachable from the accounts tab too, add a second entry
          point rather than moving this one back outside both sections. */}
      <p class="staff-tertiary">
        <Button variant="quiet" onClick={() => void downloadReview()}>
          {staffCopy.downloadReview}
        </Button>
      </p>
    </>
  );

  /** The accounts table's armed confirm — ONE block above the table for both
   *  kinds (Deactivate, Reset PIN), the header docblock says why. `null`
   *  while nothing on this table is armed. */
  const accountConfirm = (): { item: AccountItem; kind: 'account' | 'pin' } | null => {
    if (armed === null || armed.kind === 'staff' || accounts === null) return null;
    const item = accounts.find((a) => a.id === armed.id);
    return item === undefined ? null : { item, kind: armed.kind };
  };

  const accountRow = (item: AccountItem) => {
    const isArmed = armed !== null && armed.id === item.id && armed.kind !== 'staff' ? armed.kind : null;
    // The armed row: its action button is gone (the confirm block above the
    // table carries the two buttons), which is how the row reads as armed.
    /* ⭐ T2173 — ONE ROW, not a stack. The buttons used to be siblings separated
       by `{' '}`, so they wrapped to two lines at unequal widths and spilled past
       the column and the table's right edge (Alex's production screenshots,
       2026-09-18). `.staff-actions` is the surface's own flex row, and it is
       `nowrap` so the column cannot answer by stacking the pair instead.
       ⚠️ NO per-column class is passed and none exists — an earlier draft of this
       comment named a `staff-actions-col` that was never written; see the `Table`
       docblock below for why per-column widths are refused here.
       ⛔ Reset PIN is `secondary`, NEVER `primary`: `staff.spec.tsx` asserts the
       accounts section renders no `.btn--primary` at all. Deactivate stays
       `destructive-quiet` — the destructive text button, per DESIGN.md §5.1. */
    const actions = (
      <div class="staff-actions">
        {item.active && isArmed !== 'pin' && (
          <Button variant="secondary" size="inline" ariaLabel={staffCopy.resetPinFor(item.email)} onClick={() => setArmed({ kind: 'pin', id: item.id })}>
            {staffCopy.resetPin}
          </Button>
        )}
        {item.role !== 'admin' && item.active && isArmed !== 'account' && (
          <Button
            variant="destructive-quiet"
            size="inline"
            ariaLabel={staffCopy.deactivateAccountFor(item.email)}
            onClick={() => setArmed({ kind: 'account', id: item.id })}
          >
            {staffCopy.deactivateAccount}
          </Button>
        )}
        {item.role !== 'admin' && !item.active && (
          <Button variant="quiet" size="inline" ariaLabel={staffCopy.reactivateAccountFor(item.email)} onClick={() => void setAccountActive(item, true)}>
            {staffCopy.reactivateAccount}
          </Button>
        )}
      </div>
    );
    return {
      key: String(item.id),
      cells: [
        item.email,
        staffCopy.types[item.role] ?? item.role,
        item.groupLabel ?? '',
        item.active ? staffCopy.yes : staffCopy.no,
        item.hasPin ? staffCopy.yes : staffCopy.no,
        actions,
      ],
    };
  };

  const confirm = accountConfirm();
  const accountsSection = (
    <>
      <p class="field-hint">{staffCopy.accountsIntro}</p>
      <h2 class="my-requests-group">{staffCopy.sectionAccounts}</h2>
      {/* ⭐ T2075 M6 — the armed confirm, full-width and ABOVE the table, so
          its two buttons sit inside the plane whatever the list's length and
          whichever row is armed (the M5 ux-review carry; header docblock). */}
      {confirm !== null && (
        <div class="staff-confirm" ref={confirmRef} tabIndex={-1}>
          <span>
            {confirm.kind === 'account'
              ? staffCopy.deactivateAccountArmed(confirm.item.email)
              : staffCopy.resetPinArmed(confirm.item.email)}
          </span>
          {confirm.kind === 'account' ? (
            <Button variant="destructive-quiet" size="inline" onClick={() => void setAccountActive(confirm.item, false)}>
              {staffCopy.confirmDeactivateAccount}
            </Button>
          ) : (
            <Button variant="destructive-quiet" size="inline" onClick={() => void resetPin(confirm.item)}>
              {staffCopy.confirmResetPin}
            </Button>
          )}
          <Button variant="quiet" size="inline" onClick={() => setArmed(null)}>
            {staffCopy.cancel}
          </Button>
        </div>
      )}
      {accounts === null ? (
        <p class="loading-notice" role="status">
          {sharedCopy.loading}
        </p>
      ) : (
        <TableScroll label={staffCopy.accountsScrollLabel}>
          <Table
            label={staffCopy.accountsTableLabel}
            /* ⛔ NO PER-COLUMN `cellClass` WIDTHS, and that is a MEASURED refusal,
               not an omission. `app.css` records that a per-column width block was
               tried on `.ops-table` and REMOVED because under `auto` table-layout
               the preferences measured byte-identical with and without them — the
               intrinsic content widths win. `table-layout: fixed` would make them
               bite, but it can only be set on the `<table>`, which `Table` owns and
               a screen sheet may not reach into (DESIGN.md §5.1), so it is a
               library/board decision rather than a screen one.
               ⇒ T2173 narrows this table the way the intrinsic model allows: the
               Actions cell is a `nowrap` flex row, which raises the column's
               MIN-content width to the pair's full width, and auto layout must
               honour a min-content floor — so the column is forced wide enough to
               hold both on one line and the surplus comes off Mailbox, which can
               wrap. Narrow viewports overflow into `TableScroll`, the named scroll
               region this table already sits in.
               ⚠️ A first attempt used `flex-wrap: wrap`, which did NOT work and is
               worth recording: a wrapping row's MIN-content is only its widest
               item, so the column stayed at 200px and simply stacked the pair
               tidily. Measured on the seat, that left the four short columns
               unchanged and the table 20px taller. Grade this by measuring the real
               column widths at 1024 and 1366, never by reading this comment. */
            columns={[
              { head: staffCopy.colMailbox, cellClass: 'staff-mailbox-col' },
              { head: staffCopy.colType },
              { head: staffCopy.colAccountGroup },
              { head: staffCopy.colActive, cellClass: 'staff-flag-col' },
              { head: staffCopy.colPin, cellClass: 'staff-flag-col' },
              { head: staffCopy.colActions },
            ]}
            rows={accounts.map(accountRow)}
            empty={{ headline: staffCopy.accountsEmpty }}
          />
        </TableScroll>
      )}
      {/* The add row sits UNDER the table on this section (the header docblock
          says why): its Add is secondary, so the plane rule does not place it,
          and the table gains the row's height for an armed confirm.
          ⭐ T2173 rework 1 — `.staff-addblock` separates it as its OWN form block
          rather than leaving it reading as a pseudo-row of the table. Captain
          measured its Group select 66px right of the table's Group column; the two
          grids divide space by different rules (field count vs content), so they
          cannot be aligned by tuning one — the honest fix is to stop implying the
          relationship. */}
      <div class="staff-row staff-addblock">
        <FormField
          id="staff-account-mailbox"
          type="email"
          label={staffCopy.addMailboxLabel}
          value={mailbox}
          onInput={setMailbox}
          error={mailboxError}
          autoComplete="off"
          inputMode="email"
          maxLength={MAILBOX_MAX_LEN}
          opaque
        />
        <FormField
          id="staff-account-type"
          kind="select"
          label={staffCopy.addTypeLabel}
          value={accountType}
          onInput={(v) => setAccountType(v === 'reception' ? 'reception' : 'group')}
          options={[
            { value: 'group', label: staffCopy.types['group']! },
            { value: 'reception', label: staffCopy.types['reception']! },
          ]}
        />
        {accountType === 'group' && (
          <FormField
            id="staff-account-group"
            kind="select"
            label={staffCopy.addAccountGroupLabel}
            value={accountGroup}
            onInput={setAccountGroup}
            options={groupOptions}
            error={accountGroupError}
            describedBy={groups.length === 0 ? [ACCOUNT_GROUP_NOTE_ID] : undefined}
            disabled={groups.length === 0}
          />
        )}
        <Button variant="secondary" onClick={() => void addAccount()}>
          {staffCopy.addAccountButton}
        </Button>
      </div>
      {accountType === 'group' && groups.length === 0 && (
        <p class="field-hint" id={ACCOUNT_GROUP_NOTE_ID}>
          {staffCopy.addAccountNoGroups}
        </p>
      )}
    </>
  );

  return (
    <div class="screen staff-screen">
      <h1>{staffCopy.title}</h1>
      <div class="tab-strip tab-strip--section" role="group" aria-label={staffCopy.sectionLegend}>
        <Button variant="tab" ariaPressed={section === 'members'} onClick={() => setSection('members')}>
          {staffCopy.sectionMembers}
        </Button>
        <Button variant="tab" ariaPressed={section === 'accounts'} onClick={() => setSection('accounts')}>
          {staffCopy.sectionAccounts}
        </Button>
      </div>
      {/* What the last act DID, announced politely: one status line, always
          present — for the acts that render no Notice of their own (add, edit,
          deactivate, reactivate, add sign-in, PIN reset). The import speaks
          through its report Notice instead, so the two regions never carry
          one event. */}
      <p class="visually-hidden" role="status">
        {status ?? ''}
      </p>
      {actError !== null && <Notice kind="error">{actError}</Notice>}
      {section === 'members' ? members : accountsSection}
    </div>
  );
}
